<template>
  <ul v-if="shouldRender" class="overflow-shadows-horizontal">
    <!-- All filter (always shown) -->
    <li>
      <input
        type="checkbox"
        name="resource-filter"
        id="filter-all"
        class="visuallyhidden"
        :checked="isAllSelected"
        @change="handleFilterChange('all', $event)"
      />
      <label for="filter-all">All</label>
    </li>
    <!-- Dynamic filters based on index results -->
    <li v-for="item in visibleFilters" :key="item.index">
      <input
        type="checkbox"
        name="resource-filter"
        :id="`filter-${item.index}`"
        class="visuallyhidden"
        :checked="selectedFilters.includes(item.index)"
        @change="handleFilterChange(item.index, $event)"
      />
      <label :for="`filter-${item.index}`">{{ item.displayName }}</label>
    </li>
  </ul>
</template>
<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from "vue";
import { liteClient as algoliasearch } from "algoliasearch/lite";

const props = defineProps({
  applicationId: {
    type: String,
    default: "",
  },
  searchApiKey: {
    type: String,
    default: "",
  },
  indexPrefix: {
    type: String,
    default: "",
  },
  indexes: {
    type: [Array, Object],
    default: () => [],
  },
});

// Convert indexes to standardized format
const indexList = computed(() => {
  if (Array.isArray(props.indexes) && props.indexes.length > 0) {
    // Check if it's the new array-of-objects format
    if (typeof props.indexes[0] === "object" && props.indexes[0].index) {
      // New format: [{ label: 'Blog', index: 'blog', section: 'resourcesBlog' }]
      return props.indexes.map((item) => ({
        index: item.index,
        displayName: item.label,
        section: item.section,
        nbHits: 0,
      }));
    }
  } else if (
    typeof props.indexes === "object" &&
    !Array.isArray(props.indexes)
  ) {
    // Legacy object format: { 'blog': 'Blog', 'webinars': 'Webinars' }
    return Object.entries(props.indexes).map(([index, displayName]) => ({
      index,
      displayName,
      section: null,
      nbHits: 0,
    }));
  }
  return [];
});

// Check if all required props are provided and valid
const shouldRender = computed(() => {
  return !!(
    props.applicationId &&
    props.searchApiKey &&
    props.indexPrefix &&
    props.indexes &&
    indexList.value.length > 0
  );
});

const searchClient = computed(() => {
  if (!shouldRender.value) return null;
  return algoliasearch(props.applicationId, props.searchApiKey);
});

const getIndexName = (index) => {
  return `${props.indexPrefix}_${index}`;
};

// Store filters with their hit counts
const filtersWithCounts = ref([]);

// Computed property for filters that have results
const visibleFilters = computed(() => {
  return filtersWithCounts.value.filter((item) => item.nbHits > 0);
});

// Track selected filters from URL
const selectedFilters = ref([]);

// Computed property to check if "All" is selected
const isAllSelected = computed(() => {
  return selectedFilters.value.length === 0;
});

// Parse filters from URL hash
const parseFiltersFromUrl = () => {
  const hash = window.location.hash.substring(1); // Remove the # character

  if (hash) {
    // Split by + to get individual filters
    selectedFilters.value = hash.split("+").filter((f) => f.trim());
  } else {
    // No filters in hash, "All" is selected
    selectedFilters.value = [];
  }
};

// Build hash from selected filters
const buildHash = (filters) => {
  if (filters.length === 0) {
    return "";
  }

  return filters.join("+");
};

// Handle filter checkbox changes
const handleFilterChange = (filter, event) => {
  let newFilters = [...selectedFilters.value];

  if (filter === "all") {
    // "All" was clicked - clear all filters
    newFilters = [];
  } else {
    // Individual filter was clicked
    if (event.target.checked) {
      // Add filter if not already present
      if (!newFilters.includes(filter)) {
        newFilters.push(filter);
        // Sort to maintain consistent order from config
        const indexOrder = indexList.value.map((item) => item.index);
        newFilters.sort(
          (a, b) => indexOrder.indexOf(a) - indexOrder.indexOf(b),
        );
      }
    } else {
      // Remove filter
      newFilters = newFilters.filter((f) => f !== filter);
    }
  }

  // Update hash (without page reload)
  const newHash = buildHash(newFilters);

  if (newHash) {
    window.location.hash = newHash;
  } else {
    // Clear hash using history.replaceState to avoid scroll jump in Safari
    const url = window.location.pathname + window.location.search;
    history.replaceState(null, "", url);
    // Manually dispatch hashchange event so other components can react
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  }

  // Update selected filters
  selectedFilters.value = newFilters;
};

// Fetch hit counts for all indexes
const fetchHitCounts = async () => {
  if (!searchClient.value) return;

  try {
    // Create search queries for all indexes
    const queries = indexList.value.map((item) => ({
      indexName: getIndexName(item.index),
      params: {
        query: "",
        hitsPerPage: 0,
      },
    }));

    // Perform multi-index search
    const results = await searchClient.value.search(queries);

    // Map results back to index list with hit counts
    filtersWithCounts.value = indexList.value.map((item, idx) => ({
      ...item,
      nbHits: results.results[idx]?.nbHits || 0,
    }));
  } catch (error) {
    console.error("Error fetching Algolia hit counts:", error);
    filtersWithCounts.value = [];
  }
};

// Listen for hash changes (back/forward buttons)
const handleHashChange = () => {
  parseFiltersFromUrl();
};

// Initialize component
onMounted(() => {
  if (shouldRender.value) {
    parseFiltersFromUrl();
    fetchHitCounts();

    // Listen for hash changes
    window.addEventListener("hashchange", handleHashChange);
  }
});

// Cleanup
onBeforeUnmount(() => {
  window.removeEventListener("hashchange", handleHashChange);
});
</script>
